/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  getU16Decoder,
  getU16Encoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from '@solana/kit';
import {
  getAccountMetaFactory,
  type ResolvedInstructionAccount,
} from '@solana/program-client-core';
import { INITIALIZER_PROGRAM_ADDRESS } from '../programs';

export const SET_FEE_POLICY_DISCRIMINATOR = new Uint8Array([
  91, 32, 224, 109, 147, 98, 71, 132,
]);

export function getSetFeePolicyDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    SET_FEE_POLICY_DISCRIMINATOR,
  );
}

export type SetFeePolicyInstruction<
  TProgram extends string = typeof INITIALIZER_PROGRAM_ADDRESS,
  TAccountConfig extends string | AccountMeta<string> = string,
  TAccountAdmin extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountConfig extends string
        ? WritableAccount<TAccountConfig>
        : TAccountConfig,
      TAccountAdmin extends string
        ? ReadonlySignerAccount<TAccountAdmin> &
            AccountSignerMeta<TAccountAdmin>
        : TAccountAdmin,
      ...TRemainingAccounts,
    ]
  >;

export type SetFeePolicyInstructionData = {
  discriminator: ReadonlyUint8Array;
  protocolFeeBps: number;
  minSwapFeeBps: number;
  maxSwapFeeBps: number;
};

export type SetFeePolicyInstructionDataArgs = {
  protocolFeeBps: number;
  minSwapFeeBps: number;
  maxSwapFeeBps: number;
};

export function getSetFeePolicyInstructionDataEncoder(): FixedSizeEncoder<SetFeePolicyInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['protocolFeeBps', getU16Encoder()],
      ['minSwapFeeBps', getU16Encoder()],
      ['maxSwapFeeBps', getU16Encoder()],
    ]),
    (value) => ({ ...value, discriminator: SET_FEE_POLICY_DISCRIMINATOR }),
  );
}

export function getSetFeePolicyInstructionDataDecoder(): FixedSizeDecoder<SetFeePolicyInstructionData> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['protocolFeeBps', getU16Decoder()],
    ['minSwapFeeBps', getU16Decoder()],
    ['maxSwapFeeBps', getU16Decoder()],
  ]);
}

export function getSetFeePolicyInstructionDataCodec(): FixedSizeCodec<
  SetFeePolicyInstructionDataArgs,
  SetFeePolicyInstructionData
> {
  return combineCodec(
    getSetFeePolicyInstructionDataEncoder(),
    getSetFeePolicyInstructionDataDecoder(),
  );
}

export type SetFeePolicyAsyncInput<
  TAccountConfig extends string = string,
  TAccountAdmin extends string = string,
> = {
  config?: Address<TAccountConfig>;
  admin: TransactionSigner<TAccountAdmin>;
  protocolFeeBps: SetFeePolicyInstructionDataArgs['protocolFeeBps'];
  minSwapFeeBps: SetFeePolicyInstructionDataArgs['minSwapFeeBps'];
  maxSwapFeeBps: SetFeePolicyInstructionDataArgs['maxSwapFeeBps'];
};

export async function getSetFeePolicyInstructionAsync<
  TAccountConfig extends string,
  TAccountAdmin extends string,
  TProgramAddress extends Address = typeof INITIALIZER_PROGRAM_ADDRESS,
>(
  input: SetFeePolicyAsyncInput<TAccountConfig, TAccountAdmin>,
  config?: { programAddress?: TProgramAddress },
): Promise<
  SetFeePolicyInstruction<TProgramAddress, TAccountConfig, TAccountAdmin>
> {
  // Program address.
  const programAddress = config?.programAddress ?? INITIALIZER_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    config: { value: input.config ?? null, isWritable: true },
    admin: { value: input.admin ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.config.value) {
    accounts.config.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(new Uint8Array([99, 111, 110, 102, 105, 103])),
      ],
    });
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('config', accounts.config),
      getAccountMeta('admin', accounts.admin),
    ],
    data: getSetFeePolicyInstructionDataEncoder().encode(
      args as SetFeePolicyInstructionDataArgs,
    ),
    programAddress,
  } as SetFeePolicyInstruction<TProgramAddress, TAccountConfig, TAccountAdmin>);
}

export type SetFeePolicyInput<
  TAccountConfig extends string = string,
  TAccountAdmin extends string = string,
> = {
  config: Address<TAccountConfig>;
  admin: TransactionSigner<TAccountAdmin>;
  protocolFeeBps: SetFeePolicyInstructionDataArgs['protocolFeeBps'];
  minSwapFeeBps: SetFeePolicyInstructionDataArgs['minSwapFeeBps'];
  maxSwapFeeBps: SetFeePolicyInstructionDataArgs['maxSwapFeeBps'];
};

export function getSetFeePolicyInstruction<
  TAccountConfig extends string,
  TAccountAdmin extends string,
  TProgramAddress extends Address = typeof INITIALIZER_PROGRAM_ADDRESS,
>(
  input: SetFeePolicyInput<TAccountConfig, TAccountAdmin>,
  config?: { programAddress?: TProgramAddress },
): SetFeePolicyInstruction<TProgramAddress, TAccountConfig, TAccountAdmin> {
  // Program address.
  const programAddress = config?.programAddress ?? INITIALIZER_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    config: { value: input.config ?? null, isWritable: true },
    admin: { value: input.admin ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('config', accounts.config),
      getAccountMeta('admin', accounts.admin),
    ],
    data: getSetFeePolicyInstructionDataEncoder().encode(
      args as SetFeePolicyInstructionDataArgs,
    ),
    programAddress,
  } as SetFeePolicyInstruction<TProgramAddress, TAccountConfig, TAccountAdmin>);
}

export type ParsedSetFeePolicyInstruction<
  TProgram extends string = typeof INITIALIZER_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    config: TAccountMetas[0];
    admin: TAccountMetas[1];
  };
  data: SetFeePolicyInstructionData;
};

export function parseSetFeePolicyInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedSetFeePolicyInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 2) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 2,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: { config: getNextAccount(), admin: getNextAccount() },
    data: getSetFeePolicyInstructionDataDecoder().decode(instruction.data),
  };
}
